import React from 'react';

/**
 * Shared, reusable profile-mode selector — two clickable "cards" that behave
 * like radio buttons, plus a checkbox that simply reflects the selected mode
 * (kept for accessibility/keyboard users; the cards are the primary control).
 *
 * Reused across: Signup onboarding (OnboardingProfilePage), Edit Profile
 * (EditProfileModal), and Add New Member (App.js) — one implementation,
 * no duplicated selector logic.
 *
 * value: 'complete' | 'name_analysis'
 * onChange: (nextValue) => void
 */
export default function ProfileTypeSelector({ value, onChange, compact = false, disabled = false }) {
  const isNameAnalysis = value === 'name_analysis';

  const select = (mode) => {
    if (disabled) return;
    onChange(mode);
  };

  const cardBase = {
    flex: 1, textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
    padding: compact ? '0.7rem 0.85rem' : '0.95rem 1.1rem',
    borderRadius: '0.85rem', transition: 'all .15s', opacity: disabled ? 0.6 : 1,
  };

  return (
    <div style={{ marginBottom: compact ? '1rem' : '1.5rem' }}>
      <div style={{ display: 'flex', gap: '0.65rem', marginBottom: '0.65rem' }}>
        <div
          role="button" tabIndex={0}
          onClick={() => select('complete')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select('complete'); }}
          style={{
            ...cardBase,
            border: !isNameAnalysis ? '2px solid #d97706' : '1.5px solid #fde68a',
            background: !isNameAnalysis ? '#fff7ed' : '#fffbeb',
            boxShadow: !isNameAnalysis ? '0 4px 14px rgba(180,83,9,0.12)' : 'none',
          }}
        >
          <p style={{ margin: 0, fontSize: compact ? '0.82rem' : '0.9rem', fontWeight: 700, color: '#78350f' }}>🪐 Complete Kundli</p>
          {!compact && (
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#a16207', lineHeight: 1.4 }}>
              Full Vedic birth chart from your birth details
            </p>
          )}
        </div>

        <div
          role="button" tabIndex={0}
          onClick={() => select('name_analysis')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') select('name_analysis'); }}
          style={{
            ...cardBase,
            border: isNameAnalysis ? '2px solid #d97706' : '1.5px solid #fde68a',
            background: isNameAnalysis ? '#fff7ed' : '#fffbeb',
            boxShadow: isNameAnalysis ? '0 4px 14px rgba(180,83,9,0.12)' : 'none',
          }}
        >
          <p style={{ margin: 0, fontSize: compact ? '0.82rem' : '0.9rem', fontWeight: 700, color: '#78350f' }}>✨ Name Analysis</p>
          {!compact && (
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#a16207', lineHeight: 1.4 }}>
              Personalised insights from your name only
            </p>
          )}
        </div>
      </div>

      {/* Reflects the selected mode — clicking it also toggles for keyboard/a11y users. */}
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '0.78rem', color: '#78350f',
      }}>
        <input
          type="checkbox" checked={isNameAnalysis} disabled={disabled}
          onChange={(e) => select(e.target.checked ? 'name_analysis' : 'complete')}
          style={{ marginTop: 3, width: 15, height: 15, accentColor: '#b45309' }}
        />
        <span>I don't know my birth details</span>
      </label>
    </div>
  );
}
